import type { EnrollmentTerm, School } from "@/models/school";
import type { Option } from "@/models";
import type { PelLoanConfigurationDetails } from "@/models/pel";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import { compareMonthAndYear } from "@/use/dateHelpers";

export default function useSchoolConfirmationForm(
  schoolInfo: Ref<Record<string, any>>,
  options?: {
    schoolEditable?: boolean;
    academicTermEditable?: boolean;
    enrollmentStatusEditable?: boolean;
  },
) {
  const store = useStore();

  const invalidEnrollmentTerm = ref<boolean>(false);

  const { formatGraduationDateDisplay } = useFormatter();

  const academicTermOptions = computed<Option[]>(() => store.state.enrollmentTermOptions || []);

  const details = computed<PelLoanConfigurationDetails>(
    () => store.state.loanConfigurationModule.applicationInfo,
  );

  const enrollmentStatuses = computed<Option[]>(() => store.state.enrollmentStatuses || []);

  const enrollmentStatusOptions = computed(() => {
    return schoolInfo.value?.academicTermId &&
      selectedEnrollmentTerm.value?.description?.toLowerCase()?.includes("summer")
      ? enrollmentStatuses.value
      : enrollmentStatuses.value?.filter((o: Option) => o.label !== "Less Than Half-Time");
  });

  const enrollmentTermChanged = computed<boolean>(() => {
    return previouslySelectedEnrollmentTerm.value?.id !== selectedEnrollmentTerm.value?.id;
  });

  const enrollmentStatusChanged = computed<boolean>(() => {
    return (
      previouslySelectedEnrollmentStatus.value?.value !== selectedEnrollmentStatus.value?.value
    );
  });

  const gradDateChanged = computed<boolean>(() => {
    return updatedGradDateInfo.value?.display !== originalGradDateInfo.value?.display;
  });

  const hasChanges = computed<boolean | undefined>(() => {
    return (
      enrollmentTermChanged.value === true ||
      enrollmentStatusChanged.value === true ||
      gradDateChanged.value === true ||
      schoolChanged.value === true
    );
  });

  const loanPeriodEndDate = computed<Date | null>(() => {
    return selectedEnrollmentTerm.value?.fundsEndDate || null;
  });

  const originalGradDateInfo = computed(() => {
    if (store.state.loanConfigurationModule?.applicationInfo?.graduationDate) {
      const date = new Date(store.state.loanConfigurationModule?.applicationInfo?.graduationDate);

      return getGraduationDateDetails(date);
    }

    return undefined;
  });

  const previouslySelectedSchool = computed<School | null>(() => {
    return store.state.loanConfigurationModule?.applicationInfo?.selectedSchool;
  });

  const previouslySelectedEnrollmentStatus = computed<Option>(() =>
    store.state.enrollmentStatuses?.find(
      (e: Option) =>
        e.value ===
        Number(store?.state?.appData?.application?.schoolConfiguration?.enrollmentStatus),
    ),
  );

  const previouslySelectedEnrollmentTerm = computed<EnrollmentTerm>(
    () => store.state.loanConfigurationModule.applicationInfo.selectedEnrollmentTerm,
  );

  const schoolEditable = computed<boolean>(() => {
    if (options) {
      return options?.schoolEditable === true;
    }

    return true;
  });

  const academicTermEditable = computed<boolean>(() => {
    if (options) {
      return options?.academicTermEditable === true;
    }

    return true;
  });

  const selectedEnrollmentTerm = computed<EnrollmentTerm | undefined>(() => {
    if (!academicTermEditable.value) {
      return previouslySelectedEnrollmentTerm.value;
    }
    return store.state.enrollmentTerms?.find(
      (e: EnrollmentTerm) => e.id === Number(schoolInfo.value?.academicTermId),
    );
  });

  const selectedEnrollmentStatus = computed<Option>(() => {
    return store.state.enrollmentStatuses?.find(
      (e: Option) => e.value === Number(schoolInfo.value?.enrollmentStatusId),
    );
  });

  const selectedGraduationDateISOStr = computed<string | undefined>(() => {
    if (schoolInfo.value.graduationDateMonth && schoolInfo.value?.graduationDateYear) {
      return `${schoolInfo.value?.graduationDateYear}-${schoolInfo.value.graduationDateMonth?.toString()?.padStart(2, "0")}-${originalGradDateInfo.value?.graduationDateDay?.toString()?.padStart(2, "0") || 15}T00:00:00`;
    }
    return undefined;
  });
  const selectedGraduationDate = computed<Date | undefined>(() => {
    if (selectedGraduationDateISOStr.value) {
      return new Date(selectedGraduationDateISOStr.value);
    }
    return undefined;
  });
  const selectedSchool = computed<School | undefined>(() => {
    if (!schoolEditable.value) {
      return previouslySelectedSchool.value;
    }

    return store.state.schools?.find(
      (s: School) =>
        s.id === Number(schoolInfo.value.school?.id) &&
        s.schoolDetailsId === Number(schoolInfo.value?.school?.schoolDetailsId),
    );
  });

  const schoolChanged = computed<boolean>(() => {
    const originalSchool = {
      schoolName: previouslySelectedSchool.value?.schoolName || "",
      schoolDetailsId: previouslySelectedSchool.value?.schoolDetailsId || "",
      schoolBranchDOEId: previouslySelectedSchool.value?.schoolBranchDOEId || "",
      schoolDOEId: previouslySelectedSchool.value?.schoolDOEId || "",
    } as Partial<School>;

    const updatedSchool = {
      schoolName: schoolInfo.value?.school?.schoolName || "",
      schoolDetailsId: schoolInfo.value?.school?.schoolDetailsId || "",
      schoolBranchDOEId: schoolInfo.value?.school?.schoolBranchDOEId || "",
      schoolDOEId: schoolInfo.value?.school?.schoolDOEId || "",
    } as Partial<School>;

    return !(originalSchool?.schoolDetailsId === updatedSchool?.schoolDetailsId);
  });

  const updatedGradDateInfo = computed(() => {
    if (selectedGraduationDate.value) {
      return getGraduationDateDetails(selectedGraduationDate.value);
    }

    return undefined;
  });

  const anticipatedGraduationDate = computed<Date | null>(
    () => updatedGradDateInfo.value?.graduationDate || null,
  );

  const validEnrollmentTerm = computed<boolean>(() => {
    if (loanPeriodEndDate.value && selectedGraduationDate.value) {
      return isValidAcademicTerm(selectedGraduationDate.value, new Date(loanPeriodEndDate.value));
    }
    return true;
  });
  function getGraduationDateDetails(dateInput: Date) {
    const year = dateInput.getUTCFullYear();
    const month = dateInput.getUTCMonth() + 1;
    const day = dateInput.getUTCDate();
    const twoDigitMonth = (dateInput.getUTCMonth() + 1).toString().padStart(2, "0");
    const twoDigitDay = dateInput.getUTCDate().toString().padStart(2, "0");

    return {
      graduationDateDay: day,
      graduationDateMonth: month,
      graduationDateYear: year,
      graduationDate: dateInput,
      graduationDateStr: `${year}-${twoDigitMonth}-${twoDigitDay}`,
      graduationDateISO: `${year}-${twoDigitMonth}-${twoDigitDay}T00:00:00`,
      display: formatGraduationDateDisplay(dateInput),
    };
  }

  function isValidAcademicTerm(graduationDate: Date, fundsEndDate: Date) {
    const comparison = compareMonthAndYear(graduationDate, fundsEndDate);

    // Graduation Date is Before FundsEndDate
    if (comparison === -1) {
      return false;
    }

    return true;
  }

  watch(validEnrollmentTerm, (newVal: boolean, oldVal: boolean) => {
    if (oldVal === false && newVal === true) {
      invalidEnrollmentTerm.value = false;
    }
  });

  return {
    academicTermOptions,
    anticipatedGraduationDate,
    details,
    enrollmentStatuses,
    enrollmentStatusOptions,
    enrollmentStatusChanged,
    enrollmentTermChanged,
    gradDateChanged,
    hasChanges,
    invalidEnrollmentTerm,
    isValidAcademicTerm,
    loanPeriodEndDate,
    originalGradDateInfo,
    previouslySelectedEnrollmentStatus,
    previouslySelectedEnrollmentTerm,
    previouslySelectedSchool,
    selectedSchool,
    selectedEnrollmentTerm,
    selectedEnrollmentStatus,
    selectedGraduationDate,
    selectedGraduationDateISOStr,
    schoolChanged,
    updatedGradDateInfo,
    validEnrollmentTerm,
  };
}
